import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import VehicleImage from '../../components/base/VehicleImage';
import { vehicles } from '../../mocks/vehicles';

export default function FleetPage() {
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [priceRange, setPriceRange] = useState('Todos');
  const [selectedDuration, setSelectedDuration] = useState<'30min' | '1h' | '3h' | '6h' | '12h' | '24h'>('24h');

  const categories = ['Todos', 'SUV de Lujo', 'Deportivo de Lujo', 'Superdeportivo', 'Deportivo', 'SUV Deportivo', 'SUV Premium'];
  const priceRanges = [
    { label: 'Todos', min: 0, max: Infinity },
    { label: '$500 - $1000', min: 500, max: 1000 },
    { label: '$1000 - $1500', min: 1000, max: 1500 },
    { label: '$1500 - $2000', min: 1500, max: 2000 },
    { label: '$2000+', min: 2000, max: Infinity }
  ];

  const durationLabels = {
    '30min': '30 Min',
    '1h': '1 Hora',
    '3h': '3 Horas',
    '6h': '6 Horas',
    '12h': '12 Horas',
    '24h': '24 Horas'
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    const categoryMatch = selectedCategory === 'Todos' || vehicle.category === selectedCategory;
    const currentPrice = vehicle.pricing[selectedDuration];
    const selectedRange = priceRanges.find(r => r.label === priceRange);
    const priceMatch = !selectedRange || (currentPrice >= selectedRange.min && currentPrice <= selectedRange.max);
    return categoryMatch && priceMatch;
  });

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://images.weserv.nl/?url=sunandspeed.com/wp-content/uploads/2025/04/PRINCIPAL-lamborghini-6-1.webp&output=webp&q=80&n=-1"
            alt="Luxury Fleet"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-4">
            Nuestra <span className="text-amber-400">Flota Exclusiva</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Descubre la colección más prestigiosa de vehículos de lujo disponibles para alquiler
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-gray-900 py-8 sticky top-20 z-40 border-b border-amber-900/30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Category Filter */}
            <div className="flex-1">
              <label className="block text-amber-400 font-semibold mb-2 text-sm">
                Categoría
              </label>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      selectedCategory === category
                        ? 'bg-amber-500 text-black'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Duration Filter */}
            <div className="lg:w-64">
              <label className="block text-amber-400 font-semibold mb-2 text-sm">
                Duración
              </label>
              <select
                value={selectedDuration}
                onChange={(e) => setSelectedDuration(e.target.value as typeof selectedDuration)}
                className="w-full bg-white/10 text-white border border-amber-900/30 rounded-lg px-4 py-2 focus:outline-none focus:border-amber-500 cursor-pointer pr-8"
              >
                {(Object.keys(durationLabels) as Array<keyof typeof durationLabels>).map((duration) => (
                  <option key={duration} value={duration}>
                    {durationLabels[duration]}
                  </option>
                ))}
              </select>
            </div>

            {/* Price Filter */}
            <div className="lg:w-64">
              <label className="block text-amber-400 font-semibold mb-2 text-sm">
                Rango de Precio
              </label>
              <select
                value={priceRange}
                onChange={(e) => setPriceRange(e.target.value)}
                className="w-full bg-white/10 text-white border border-amber-900/30 rounded-lg px-4 py-2 focus:outline-none focus:border-amber-500 cursor-pointer pr-8"
              >
                {priceRanges.map((range) => (
                  <option key={range.label} value={range.label}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 text-gray-400 text-sm">
            Mostrando {filteredVehicles.length} vehículo{filteredVehicles.length !== 1 ? 's' : ''} - Precios por {durationLabels[selectedDuration]}
          </div>
        </div>
      </section>

      {/* Vehicle Grid */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4">
          {filteredVehicles.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-car-line text-6xl text-gray-600 mb-4"></i>
              <h3 className="text-2xl font-bold text-white mb-2">
                No se encontraron vehículos
              </h3>
              <p className="text-gray-400 mb-6">
                Intenta ajustar los filtros para ver más opciones
              </p>
              <button
                onClick={() => {
                  setSelectedCategory('Todos');
                  setPriceRange('Todos');
                  setSelectedDuration('24h');
                }}
                className="bg-amber-500 text-black px-6 py-3 rounded-full font-semibold hover:bg-amber-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Limpiar Filtros
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredVehicles.map((vehicle) => {
                const currentPrice = vehicle.pricing[selectedDuration];
                return (
                  <div
                    key={vehicle.id}
                    className="bg-gray-900 rounded-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300 border border-amber-900/30"
                  >
                    <div className="relative h-64 overflow-hidden">
                      <Swiper
                        modules={[Autoplay, Pagination]}
                        autoplay={{ delay: 3000, disableOnInteraction: false }}
                        pagination={{ clickable: true }}
                        loop
                        className="h-full"
                      >
                        {vehicle.images.map((image, index) => (
                          <SwiperSlide key={index}>
                            <div className="relative h-64 bg-gray-900">
                              <VehicleImage
                                src={image}
                                alt={`${vehicle.brand} ${vehicle.model} - Foto ${index + 1}`}
                                className="w-full h-full object-cover object-center absolute inset-0"
                              />
                            </div>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                      <div className="absolute top-4 right-4 bg-amber-500 text-black px-4 py-2 rounded-full font-bold text-sm z-10">
                        ${vehicle.pricePerDay.toLocaleString()}/día
                      </div>
                    </div>

                    <div className="p-6">
                      <div className="mb-2">
                        <span className="text-amber-400 text-sm font-semibold">{vehicle.category}</span>
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-1">
                        {vehicle.brand}
                      </h3>
                      <p className="text-xl text-gray-300 mb-4">{vehicle.model}</p>
                      <p className="text-gray-400 text-sm mb-6 line-clamp-2">
                        {vehicle.description}
                      </p>

                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-400 mb-6">
                        <div className="flex items-center gap-2">
                          <i className="ri-user-line text-amber-400"></i>
                          <span>{vehicle.specifications.passengers} pasajeros</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <i className="ri-settings-line text-amber-400"></i>
                          <span>{vehicle.specifications.transmission}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <i className="ri-gas-station-line text-amber-400"></i>
                          <span>{vehicle.specifications.fuel}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <i className="ri-calendar-line text-amber-400"></i>
                          <span>{vehicle.specifications.year}</span>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Link
                          to={`/vehiculo/${vehicle.id}`}
                          className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 text-black text-center py-3 rounded-full font-bold hover:from-amber-600 hover:to-amber-700 transition-all whitespace-nowrap cursor-pointer"
                        >
                          Reservar
                        </Link>
                        <a
                          href={`https://wa.me/522291578562?text=${encodeURIComponent(`Hola! Estoy interesado en el ${vehicle.brand} ${vehicle.model} de su flota. ¿Está disponible para alquilar? Me gustaría saber precios y fechas disponibles.`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-12 h-12 flex items-center justify-center bg-green-500 text-white rounded-full hover:bg-green-600 transition-all cursor-pointer"
                        >
                          <i className="ri-whatsapp-line text-xl"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            ¿No Encuentras lo que Buscas?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Contáctanos directamente y te ayudaremos a encontrar el vehículo perfecto para tu experiencia
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contacto"
              className="bg-gradient-to-r from-amber-500 to-amber-600 text-black px-8 py-4 rounded-full font-bold hover:from-amber-600 hover:to-amber-700 transition-all whitespace-nowrap cursor-pointer"
            >
              Contactar Ahora
            </Link>
            <a
              href="https://wa.me/522291578562?text=Hola!%20Visit%C3%A9%20su%20flota%20de%20veh%C3%ADculos%20de%20lujo%20y%20necesito%20ayuda%20para%20elegir%20el%20auto%20ideal.%20%C2%BFPueden%20asesorarme%3F"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 text-white px-8 py-4 rounded-full font-bold hover:bg-green-600 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}